import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Target, TrendingUp, CheckCircle, BarChart3, Brain, ChevronRight, ChevronLeft, X, AlertTriangle } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip, Cell } from 'recharts';
import { motion, AnimatePresence } from 'framer-motion';

const IdentityModule = ({ onClose }) => {
  const { t } = useTranslation();
  const [activeView, setActiveView] = useState('overview'); // 'overview', 'diagnostic', 'dashboard'
  const [diagnosticCompleted, setDiagnosticCompleted] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load initial data
  useEffect(() => {
    const loadIdentityData = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/identity/dashboard`);
        if (response.data.has_data) {
          setDiagnosticCompleted(true);
          setDashboardData(response.data);
        }
      } catch (error) {
        console.error('Error loading identity data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadIdentityData();
  }, []);

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black z-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400">{t('identity.ui.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black z-50 overflow-hidden">
      <div className="h-screen flex flex-col">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 md:top-4 md:right-4 text-gray-500 hover:text-white transition-colors p-2 z-20"
        >
          <X className="w-5 h-5 md:w-6 md:h-6" />
        </button>

        <AnimatePresence mode="wait">
          {activeView === 'overview' && (
            <IdentityOverview 
              key="overview"
              diagnosticCompleted={diagnosticCompleted}
              dashboardData={dashboardData}
              onStartDiagnostic={() => setActiveView('diagnostic')}
              onViewDashboard={() => setActiveView('dashboard')}
            />
          )}

          {activeView === 'diagnostic' && (
            <IdentityDiagnostic 
              key="diagnostic"
              onComplete={async (submissionData) => {
                setDiagnosticCompleted(true);
                try {
                  const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/identity/dashboard`);
                  if (response.data.has_data) {
                    setDashboardData(response.data);
                    setActiveView('dashboard');
                  }
                } catch (error) {
                  console.error('Error loading dashboard data:', error);
                  setDashboardData(submissionData);
                  setActiveView('dashboard');
                }
              }}
              onBack={() => setActiveView('overview')}
            />
          )}

          {activeView === 'dashboard' && (
            <IdentityDashboard 
              key="dashboard"
              data={dashboardData}
              onBack={() => setActiveView('overview')}
              onRetake={() => setActiveView('diagnostic')}
            />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

// Page transition variants
const pageVariants = {
  initial: { opacity: 0, x: 50 },
  animate: { opacity: 1, x: 0, transition: { duration: 0.3 } },
  exit: { opacity: 0, x: -50, transition: { duration: 0.2 } }
};

// Overview Component - Dark Theme
const IdentityOverview = ({ diagnosticCompleted, dashboardData, onStartDiagnostic, onViewDashboard }) => {
  const { t } = useTranslation();

  return (
    <motion.div 
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="flex-1 flex flex-col items-center justify-center px-4 md:px-8"
    >
      {/* Hero Section */}
      <div className="text-center max-w-2xl mx-auto">
        <div className="w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4 md:mb-6">
          <Brain className="w-8 h-8 md:w-10 md:h-10 text-white" />
        </div>
        
        <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold text-white mb-2 md:mb-4">
          {t('identity.title')}
        </h1>
        <p className="text-base md:text-xl text-purple-400 mb-4 md:mb-6">
          {t('identity.description')}
        </p>
        
        {/* Status Badge */}
        {diagnosticCompleted && (
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-500/20 border border-green-500/30 rounded-full mb-6">
            <CheckCircle className="w-4 h-4 text-green-400" />
            <span className="text-green-400 text-sm font-medium">{t('identity.ui.completed')}</span>
          </div>
        )}
      </div>

      {/* Action Cards */}
      <div className="grid md:grid-cols-2 gap-4 md:gap-6 w-full max-w-2xl mt-6 md:mt-8">
        {/* Diagnostic Card */}
        <div 
          className="bg-gray-800/50 border border-purple-500/30 rounded-xl p-4 md:p-6 hover:border-purple-500/60 transition-all cursor-pointer group"
          onClick={onStartDiagnostic}
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 md:w-12 md:h-12 bg-purple-500/20 rounded-lg flex items-center justify-center">
              <BarChart3 className="w-5 h-5 md:w-6 md:h-6 text-purple-400" />
            </div>
            <div>
              <h3 className="text-base md:text-lg font-semibold text-white">{t('identity.diagnostic.title')}</h3>
              <p className="text-xs md:text-sm text-gray-400">{diagnosticCompleted ? t('identity.diagnostic.retake') : t('identity.diagnostic.start')}</p>
            </div>
          </div>
          <p className="text-sm text-gray-400 mb-4">{t('identity.diagnostic.description')}</p>
          <Button 
            className="w-full bg-purple-600 hover:bg-purple-500 text-white text-sm md:text-base"
          >
            {diagnosticCompleted ? t('identity.diagnostic.retake') : t('identity.diagnostic.start')}
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>

        {/* Dashboard Card */}
        <div 
          className={`bg-gray-800/50 border rounded-xl p-4 md:p-6 transition-all ${
            diagnosticCompleted 
              ? 'border-indigo-500/30 hover:border-indigo-500/60 cursor-pointer' 
              : 'border-gray-700/50 opacity-60'
          }`}
          onClick={diagnosticCompleted ? onViewDashboard : undefined}
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 md:w-12 md:h-12 bg-indigo-500/20 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-5 h-5 md:w-6 md:h-6 text-indigo-400" />
            </div>
            <div>
              <h3 className="text-base md:text-lg font-semibold text-white">{t('identity.dashboard.title')}</h3>
              <p className="text-xs md:text-sm text-gray-400">{t('identity.dashboard.description')}</p>
            </div>
          </div>
          {diagnosticCompleted ? (
            <Button 
              className="w-full bg-indigo-600 hover:bg-indigo-500 text-white text-sm md:text-base"
            >
              {t('identity.results.viewDashboard')}
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <p className="text-sm text-gray-500 text-center py-2">{t('identity.dashboard.completeDiagnostic')}</p>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// Identity Diagnostic Component - Dark Theme with slide-based questions
const IdentityDiagnostic = ({ onComplete, onBack }) => {
  const { t } = useTranslation();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [responses, setResponses] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Define all 30 questions (6 per dimension × 5 dimensions)
  const questions = [
    // Identity Awareness (6 questions)
    { id: 'identity_awareness_1', dimension: 'identity_awareness', text: t('identity.diagnostic.questions.identity_awareness_1') },
    { id: 'identity_awareness_2', dimension: 'identity_awareness', text: t('identity.diagnostic.questions.identity_awareness_2') },
    { id: 'identity_awareness_3', dimension: 'identity_awareness', text: t('identity.diagnostic.questions.identity_awareness_3') },
    { id: 'identity_awareness_4', dimension: 'identity_awareness', text: t('identity.diagnostic.questions.identity_awareness_4') },
    { id: 'identity_awareness_5', dimension: 'identity_awareness', text: t('identity.diagnostic.questions.identity_awareness_5') },
    { id: 'identity_awareness_6', dimension: 'identity_awareness', text: t('identity.diagnostic.questions.identity_awareness_6'), reverse: true },
    // Aspirational Identity Clarity (6 questions)
    { id: 'aspirational_clarity_1', dimension: 'aspirational_clarity', text: t('identity.diagnostic.questions.aspirational_clarity_1') },
    { id: 'aspirational_clarity_2', dimension: 'aspirational_clarity', text: t('identity.diagnostic.questions.aspirational_clarity_2') },
    { id: 'aspirational_clarity_3', dimension: 'aspirational_clarity', text: t('identity.diagnostic.questions.aspirational_clarity_3') },
    { id: 'aspirational_clarity_4', dimension: 'aspirational_clarity', text: t('identity.diagnostic.questions.aspirational_clarity_4') },
    { id: 'aspirational_clarity_5', dimension: 'aspirational_clarity', text: t('identity.diagnostic.questions.aspirational_clarity_5') },
    { id: 'aspirational_clarity_6', dimension: 'aspirational_clarity', text: t('identity.diagnostic.questions.aspirational_clarity_6'), reverse: true },
    // Purpose Depth (6 questions)
    { id: 'purpose_depth_1', dimension: 'purpose_depth', text: t('identity.diagnostic.questions.purpose_depth_1') },
    { id: 'purpose_depth_2', dimension: 'purpose_depth', text: t('identity.diagnostic.questions.purpose_depth_2') },
    { id: 'purpose_depth_3', dimension: 'purpose_depth', text: t('identity.diagnostic.questions.purpose_depth_3') },
    { id: 'purpose_depth_4', dimension: 'purpose_depth', text: t('identity.diagnostic.questions.purpose_depth_4') },
    { id: 'purpose_depth_5', dimension: 'purpose_depth', text: t('identity.diagnostic.questions.purpose_depth_5') },
    { id: 'purpose_depth_6', dimension: 'purpose_depth', text: t('identity.diagnostic.questions.purpose_depth_6'), reverse: true },
    // Gap Alignment (6 questions)
    { id: 'gap_alignment_1', dimension: 'gap_alignment', text: t('identity.diagnostic.questions.gap_alignment_1') },
    { id: 'gap_alignment_2', dimension: 'gap_alignment', text: t('identity.diagnostic.questions.gap_alignment_2') },
    { id: 'gap_alignment_3', dimension: 'gap_alignment', text: t('identity.diagnostic.questions.gap_alignment_3') },
    { id: 'gap_alignment_4', dimension: 'gap_alignment', text: t('identity.diagnostic.questions.gap_alignment_4') },
    { id: 'gap_alignment_5', dimension: 'gap_alignment', text: t('identity.diagnostic.questions.gap_alignment_5') },
    { id: 'gap_alignment_6', dimension: 'gap_alignment', text: t('identity.diagnostic.questions.gap_alignment_6'), reverse: true },
    // Identity-Behavior Consistency (6 questions)
    { id: 'identity_consistency_1', dimension: 'identity_consistency', text: t('identity.diagnostic.questions.identity_consistency_1') },
    { id: 'identity_consistency_2', dimension: 'identity_consistency', text: t('identity.diagnostic.questions.identity_consistency_2') },
    { id: 'identity_consistency_3', dimension: 'identity_consistency', text: t('identity.diagnostic.questions.identity_consistency_3') },
    { id: 'identity_consistency_4', dimension: 'identity_consistency', text: t('identity.diagnostic.questions.identity_consistency_4') },
    { id: 'identity_consistency_5', dimension: 'identity_consistency', text: t('identity.diagnostic.questions.identity_consistency_5') },
    { id: 'identity_consistency_6', dimension: 'identity_consistency', text: t('identity.diagnostic.questions.identity_consistency_6'), reverse: true },
  ];

  const currentQ = questions[currentQuestion];
  const progress = ((currentQuestion + 1) / questions.length) * 100;

  const handleAnswer = (rating) => {
    setResponses({ ...responses, [currentQ.id]: rating });
    // Auto-advance after selection
    setTimeout(() => {
      if (currentQuestion < questions.length - 1) {
        setCurrentQuestion(currentQuestion + 1);
      }
    }, 300);
  };

  const handleNext = () => {
    if (responses[currentQ.id]) {
      if (currentQuestion < questions.length - 1) {
        setCurrentQuestion(currentQuestion + 1);
      } else {
        handleSubmit();
      }
    } else {
      toast.error(t('identity.ui.selectAnswer') || 'Please select an answer');
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        responses: Object.entries(responses).map(([question_id, rating]) => ({
          question_id,
          rating: parseInt(rating)
        })),
        language: 'en'
      };

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/identity/diagnostic/submit`,
        payload
      );

      toast.success(t('identity.diagnostic.success') || 'Diagnostic completed successfully!');
      onComplete(response.data);
    } catch (error) {
      console.error('Error submitting diagnostic:', error);
      toast.error(t('identity.diagnostic.error') || 'Failed to submit diagnostic. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.div 
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="flex flex-col h-full"
    >
      {/* Progress bar */}
      <div className="flex-shrink-0 bg-black border-b border-gray-800 px-4 py-3 z-10">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-400 text-xs md:text-sm">
              {t('identity.diagnostic.title')}
            </span>
            <span className="text-gray-400 text-xs md:text-sm">
              {currentQuestion + 1} / {questions.length}
            </span>
          </div>
          <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-purple-500 to-indigo-500"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
        </div>
      </div>

      {/* Exit button */}
      <button
        onClick={onBack}
        className="absolute top-3 left-3 md:top-4 md:left-4 text-gray-500 hover:text-white transition-colors p-2 z-20 flex items-center gap-1"
      >
        <ChevronLeft className="w-5 h-5" />
        <span className="text-sm hidden md:inline">{t('identity.ui.exit')}</span>
      </button>

      {/* Question Content */}
      <div className="flex-1 flex flex-col items-center justify-center px-4 md:px-8 overflow-hidden">
        <div className="w-full max-w-xl">
          {/* Dimension Badge */}
          <div className="text-center mb-4">
            <span className="inline-block px-3 py-1 bg-purple-500/20 border border-purple-500/30 rounded-full text-purple-400 text-xs md:text-sm">
              {t(`identity.diagnostic.dimensions.${currentQ.dimension}`)}
            </span>
            {currentQ.reverse && (
              <span className="ml-2 text-xs text-amber-400">({t('identity.ui.reverseScored')})</span>
            )}
          </div>

          {/* Question Text */}
          <h2 className="text-lg md:text-2xl font-semibold text-white text-center mb-6 md:mb-8 leading-relaxed">
            {currentQ.text}
          </h2>

          {/* Likert Scale Options */}
          <div className="space-y-2 md:space-y-3">
            {[1, 2, 3, 4, 5].map((rating) => (
              <button
                key={rating}
                onClick={() => handleAnswer(rating)}
                className={`w-full p-3 md:p-4 rounded-xl text-left transition-all ${
                  responses[currentQ.id] === rating
                    ? 'bg-purple-500 border-purple-400 text-white'
                    : 'bg-gray-800/50 border border-gray-700 hover:border-purple-500/50 hover:bg-gray-800 text-gray-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-5 h-5 md:w-6 md:h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                    responses[currentQ.id] === rating
                      ? 'border-white bg-white'
                      : 'border-gray-500'
                  }`}>
                    {responses[currentQ.id] === rating && (
                      <div className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full bg-purple-500"></div>
                    )}
                  </div>
                  <span className="text-sm md:text-base">{t(`identity.diagnostic.scale.${rating}`)}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex-shrink-0 flex justify-between items-center px-4 md:px-8 pb-8 md:pb-12 pt-4 max-w-xl mx-auto w-full">
        <Button
          onClick={handleBack}
          variant="outline"
          disabled={currentQuestion === 0}
          className="bg-transparent border-gray-700 text-gray-400 hover:bg-gray-800 hover:text-white"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          {t('identity.ui.previous')}
        </Button>
        <Button
          onClick={handleNext}
          disabled={!responses[currentQ.id] || submitting}
          className="bg-purple-600 hover:bg-purple-500 text-white"
        >
          {currentQuestion === questions.length - 1 
            ? (submitting ? t('identity.diagnostic.submitting') : t('identity.diagnostic.submit'))
            : t('identity.ui.next')}
          <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </motion.div>
  );
};

// Identity Dashboard Component - Dark Theme
const IdentityDashboard = ({ data, onBack, onRetake }) => {
  const { t } = useTranslation();

  if (!data || !data.has_data) {
    return (
      <motion.div 
        variants={pageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        className="flex-1 flex flex-col items-center justify-center px-4"
      >
        <p className="text-gray-400 mb-4">{t('identity.dashboard.noData')}</p>
        <Button onClick={onBack} className="bg-gray-800 hover:bg-gray-700 text-white">
          {t('identity.ui.goBack')}
        </Button>
      </motion.div>
    );
  }

  // Prepare bar chart data
  const chartData = Object.entries(data.dimension_scores || {}).map(([dimension, score]) => {
    let barColor = '#a855f7'; // Purple default
    if (score < 2.5) barColor = '#ef4444'; // Red for weak
    else if (score < 3.5) barColor = '#f59e0b'; // Amber for developing
    else barColor = '#22c55e'; // Green for strong
    
    return {
      dimension: t(`identity.diagnostic.dimensions.${dimension}`),
      score: score,
      originalScore: score,
      color: barColor
    };
  });

  // Gap score classification
  const getGapClass = (score) => {
    if (score >= 71) return { label: t('identity.ui.strongAlignment'), color: 'text-green-400', bgColor: 'bg-green-500/20', borderColor: 'border-green-500/30' };
    if (score >= 41) return { label: t('identity.ui.moderateGap'), color: 'text-amber-400', bgColor: 'bg-amber-500/20', borderColor: 'border-amber-500/30' };
    return { label: t('identity.ui.wideGap'), color: 'text-red-400', bgColor: 'bg-red-500/20', borderColor: 'border-red-500/30' };
  };

  const gapClass = getGapClass(data.gap_score || 0);

  return (
    <motion.div 
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="flex-1 overflow-y-auto"
    >
      <div className="max-w-4xl mx-auto px-4 py-6 md:py-8">
        {/* Header */}
        <div className="flex justify-between items-start mb-6">
          <div>
            <h1 className="text-xl md:text-3xl font-bold text-white mb-1">{t('identity.dashboard.title')}</h1>
            <p className="text-sm md:text-base text-gray-400">{t('identity.dashboard.description')}</p>
          </div>
          <Button 
            onClick={onBack} 
            variant="outline" 
            className="bg-transparent border-gray-700 text-gray-400 hover:bg-gray-800 hover:text-white text-sm"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            {t('identity.ui.goBack')}
          </Button>
        </div>

        {/* Gap Score Card */}
        <div className={`${gapClass.bgColor} border ${gapClass.borderColor} rounded-xl p-4 md:p-6 mb-6`}>
          <div className="flex items-center gap-3 mb-3">
            <Target className="w-5 h-5 md:w-6 md:h-6 text-purple-400" />
            <h2 className="text-base md:text-lg font-semibold text-white">{t('identity.dashboard.gapScore')}</h2>
          </div>
          <div className="text-center">
            <div className="text-5xl md:text-6xl font-bold text-purple-400 mb-2">{Math.round(data.gap_score || 0)}</div>
            <div className={`text-lg font-semibold ${gapClass.color}`}>{gapClass.label}</div>
            <p className="text-sm text-gray-400 mt-3">{t('identity.ui.gapScoreDescription')}</p>
          </div>
        </div>

        {/* Chart Section */}
        <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-4 md:p-6 mb-6">
          <h2 className="text-base md:text-lg font-semibold text-white mb-4">{t('identity.dashboard.chartTitle')}</h2>
          <p className="text-sm text-gray-400 mb-4">{t('identity.dashboard.chartDescription')}</p>
          
          {/* Legend */}
          <div className="flex flex-wrap justify-center gap-3 md:gap-6 mb-4 p-3 bg-gray-900/50 rounded-lg">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-red-500"></div>
              <span className="text-gray-400 text-xs md:text-sm">{t('identity.ui.weakLabel')} (&lt;2.5)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-amber-500"></div>
              <span className="text-gray-400 text-xs md:text-sm">{t('identity.ui.developingLabel')} (2.5-3.5)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-green-500"></div>
              <span className="text-gray-400 text-xs md:text-sm">{t('identity.ui.strongLabel')} (&gt;3.5)</span>
            </div>
          </div>

          {/* Bar Chart */}
          <div className="w-full overflow-x-auto">
            <div className="min-w-[320px]">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart 
                  data={chartData} 
                  layout="vertical"
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" horizontal={true} vertical={false} />
                  <XAxis 
                    type="number" 
                    domain={[0, 5]}
                    ticks={[0, 1, 2, 3, 4, 5]}
                    tick={{ fill: '#9ca3af', fontSize: 12 }}
                    axisLine={{ stroke: '#4b5563' }}
                  />
                  <YAxis 
                    type="category" 
                    dataKey="dimension" 
                    width={130}
                    tick={{ fill: '#d1d5db', fontSize: 11 }}
                    axisLine={{ stroke: '#4b5563' }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#1f2937', 
                      border: '1px solid #374151',
                      borderRadius: '8px',
                      color: '#fff'
                    }}
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        return (
                          <div className="bg-gray-800 p-3 border border-gray-700 rounded-lg shadow-lg">
                            <p className="font-semibold text-white">{data.dimension}</p>
                            <p className="text-sm text-gray-300">
                              {t('identity.ui.scoreLabel')}: {data.originalScore.toFixed(2)} / 5.0
                            </p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Bar dataKey="score" barSize={20} radius={[0, 4, 4, 0]}>
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Score Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-3 mt-4">
            {chartData.map((item, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 bg-gray-900/50 rounded-lg">
                <span className="text-sm text-gray-300">{item.dimension}</span>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-bold" style={{ color: item.color }}>
                    {item.originalScore.toFixed(1)}
                  </span>
                  <span className="text-xs text-gray-500">/ 5.0</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Strengths and Vulnerabilities */}
        <div className="grid md:grid-cols-2 gap-4 md:gap-6 mb-6">
          {/* Strengths */}
          <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 md:p-5">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle className="w-5 h-5 text-green-400" />
              <h3 className="text-base font-semibold text-green-400">{t('identity.dashboard.strengths')}</h3>
            </div>
            {data.strong_dimensions && data.strong_dimensions.length > 0 ? (
              <ul className="space-y-2">
                {data.strong_dimensions.slice(0, 3).map((dimension, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-gray-300">{t(`identity.diagnostic.dimensions.${dimension}`)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-400">{t('identity.ui.noStrongDimensions')}</p>
            )}
          </div>

          {/* Vulnerabilities */}
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 md:p-5">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              <h3 className="text-base font-semibold text-red-400">{t('identity.dashboard.vulnerabilities')}</h3>
            </div>
            {data.weak_dimensions && data.weak_dimensions.length > 0 ? (
              <ul className="space-y-2">
                {data.weak_dimensions.slice(0, 3).map((dimension, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-gray-300">{t(`identity.diagnostic.dimensions.${dimension}`)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-400">{t('identity.ui.noWeakDimensions')}</p>
            )}
          </div>
        </div>

        {/* Retake Button */}
        <div className="text-center pb-8">
          <Button 
            onClick={onRetake}
            variant="outline"
            className="bg-transparent border-purple-500/50 text-purple-400 hover:bg-purple-500/10 hover:border-purple-500"
          >
            {t('identity.diagnostic.retake')}
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

export default IdentityModule;
